import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Globe, CheckCircle2, AlertCircle, Copy, ExternalLink, Loader2, Trash2,
  HelpCircle, ChevronDown, Clock, Lightbulb, FileDown, ArrowRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Custom Domain management widget. Works for BOTH the shop profile (light theme)
 * and the business page dashboard (dark theme) by parametrizing:
 *   - basePath : the API prefix for the 4 endpoints + the PDF
 *   - dark     : switch to the dark dashboard palette
 *   - target   : the destination noun shown in copy ({ accLV, nomLV, en })
 *
 * Approach: TXT ownership verification + a 302 (temporary) redirect at the
 * owner's registrar pointing their-domain.lv → kolv.lv/<target>. We use a
 * redirect instead of CNAME because the platform does not support wildcard /
 * multi-tenant custom hostnames, so the domain goes live IMMEDIATELY with no
 * KoLV-side ingress / SSL work. Free for everyone.
 */
export default function CustomDomainSection({
  language = 'lv',
  basePath = '/shop-profile/custom-domain',
  dark = false,
  target = { accLV: 'KoLV veikalu', nomLV: 'KoLV veikals', en: 'KoLV shop' },
}) {
  const isLV = language === 'lv';
  const dst = isLV ? target.accLV : target.en;      // "uz Tavu <dst>" / "to your <dst>"
  const dstNom = isLV ? target.nomLV : target.en;   // "<dstNom> ir aktīvs"
  const t = themeTokens(dark);

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [domain, setDomain] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const fetchState = async () => {
    try {
      const { data } = await axios.get(`${API}${basePath}`);
      setState(data);
    } catch (e) {
      console.error('custom-domain fetch', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchState(); }, [basePath]);  // eslint-disable-line react-hooks/exhaustive-deps

  const register = async (e) => {
    e?.preventDefault();
    if (!domain.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}${basePath}`, { custom_domain: domain.trim() });
      toast.success(isLV ? `Domēns ${data.domain} reģistrēts. Tagad pievieno DNS + pāradresāciju.` : `Domain ${data.domain} registered. Now add DNS + redirect.`);
      await fetchState();
      setDomain('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isLV ? 'Kļūda reģistrējot domēnu' : 'Failed to register domain'));
    } finally {
      setSubmitting(false);
    }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await axios.post(`${API}${basePath}/verify`);
      if (data.verified) {
        toast.success(data.message || (isLV ? 'Verificēts!' : 'Verified!'));
      } else {
        toast.error(data.message || (isLV ? 'DNS vēl nav atrasts' : 'DNS not propagated yet'));
      }
      await fetchState();
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isLV ? 'Verifikācija neizdevās' : 'Verification failed'));
    } finally {
      setVerifying(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(isLV ? 'Vai tiešām noņemt pielāgoto domēnu?' : 'Really remove custom domain?')) return;
    try {
      await axios.delete(`${API}${basePath}`);
      toast.success(isLV ? 'Domēns noņemts' : 'Domain removed');
      await fetchState();
    } catch (err) {
      toast.error(isLV ? 'Kļūda noņemot' : 'Failed to remove');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(isLV ? 'Nokopēts!' : 'Copied!');
  };

  const downloadPdf = async () => {
    try {
      const resp = await axios.get(`${API}${basePath}/instructions.pdf`, { responseType: 'blob' });
      const blob = new Blob([resp.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `KoLV-DNS-instrukcijas-${state?.domain || 'domains'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(isLV ? 'PDF lejupielādēts!' : 'PDF downloaded!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || (isLV ? 'Neizdevās lejupielādēt' : 'Failed to download'));
    }
  };

  if (loading) {
    return (
      <div className={`${t.card} rounded-2xl p-6 flex items-center justify-center`}>
        <Loader2 className="w-5 h-5 animate-spin text-[#D97736]" />
      </div>
    );
  }

  if (!state) return null;

  // State: no domain yet → add-domain form
  if (!state.has_domain) {
    return (
      <div className={`${t.card} rounded-2xl p-6`} data-testid="custom-domain-empty">
        <div className="flex items-start gap-4 mb-4">
          <div className={`w-12 h-12 rounded-xl ${t.iconWrap} flex items-center justify-center shrink-0`}>
            <Globe className="w-6 h-6 text-[#D97736]" />
          </div>
          <div className="flex-1">
            <h3 className={`font-outfit text-lg font-semibold ${t.title} mb-1`}>
              {isLV ? 'Pielāgots domēns' : 'Custom domain'}
            </h3>
            <p className={`text-sm ${t.body}`}>
              {isLV
                ? `Pievieno savu domēnu (piem., mans-veikals.lv). Mēs parādīsim, kā to novirzīt uz Tavu ${dst} — bez sarežģītas konfigurācijas.`
                : `Add your domain (e.g., my-shop.com). We will show how to redirect it to your ${dst} — no complex setup needed.`}
            </p>
          </div>
        </div>

        <HowItWorks isLV={isLV} t={t} dst={dst} />

        <form onSubmit={register} className="flex gap-2 mt-4">
          <Input
            value={domain}
            onChange={e => setDomain(e.target.value)}
            placeholder={isLV ? 'mans-veikals.lv' : 'my-shop.com'}
            className={`flex-1 ${t.input}`}
            data-testid="custom-domain-input"
          />
          <Button
            type="submit"
            disabled={!domain.trim() || submitting}
            className="bg-[#D97736] hover:bg-[#C06528] text-white"
            data-testid="custom-domain-register-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (isLV ? 'Sākt' : 'Start')}
          </Button>
        </form>
        <p className={`text-xs ${t.body} mt-2`}>
          {isLV
            ? 'Pēc reģistrācijas saņemsi precīzu TXT ierakstu un 302 pāradresācijas instrukcijas.'
            : 'After registration you will get the exact TXT record and 302 redirect instructions.'}
        </p>
      </div>
    );
  }

  // State: domain set → show TXT + redirect instructions + verify
  return (
    <div className={`${t.card} rounded-2xl p-6`} data-testid="custom-domain-active">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
            state.verified ? t.okWrap : t.warnWrap
          }`}>
            {state.verified
              ? <CheckCircle2 className={`w-6 h-6 ${t.okColor}`} />
              : <AlertCircle className={`w-6 h-6 ${t.warnColor}`} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className={`font-outfit text-lg font-semibold ${t.title} break-all`}>
                {state.domain}
              </h3>
              {state.verified ? (
                <span className={`px-2 py-0.5 text-xs font-semibold ${t.okBadge} rounded-full`}>
                  {isLV ? 'Aktīvs' : 'Live'}
                </span>
              ) : (
                <span className={`px-2 py-0.5 text-xs font-semibold ${t.warnBadge} rounded-full`}>
                  {isLV ? 'Gaida DNS' : 'Awaiting DNS'}
                </span>
              )}
            </div>
            {state.verified && (
              <p className={`text-sm ${t.okText}`}>
                {isLV
                  ? `Domēns ir verificēts. Apmeklētāji tiek novirzīti uz Tavu ${dst}.`
                  : `Domain verified. Visitors are redirected to your ${dst}.`}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={remove}
          className="text-red-500 hover:text-red-600 p-2"
          title={isLV ? 'Noņemt' : 'Remove'}
          data-testid="custom-domain-remove-btn"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {!state.verified && (
        <>
          <StepHeader stepNumber={2} totalSteps={4} isLV={isLV} t={t}
            title={isLV ? 'Pievieno TXT ierakstu (īpašumtiesības)' : 'Add TXT record (ownership)'}
            subtitle={isLV
              ? 'Atver sava domēna reģistratoru → DNS sadaļa un pievieno šo ierakstu. Tava esošā vietne netiek ietekmēta.'
              : 'Open your registrar → DNS section and add this record. Your existing site is not affected.'} />

          <div className={`${t.soft} rounded-xl p-4 mb-4`}>
            <DnsRecord
              type="TXT"
              name={state.txt_record_name}
              value={state.token}
              ttl="3600"
              onCopy={copyToClipboard}
              t={t}
              hint={isLV ? 'Apstiprina, ka šis domēns pieder Tev' : 'Confirms you own this domain'}
            />
          </div>

          <StepHeader stepNumber={3} totalSteps={4} isLV={isLV} t={t}
            title={isLV ? 'Izveido 302 pāradresāciju' : 'Set up a 302 redirect'}
            subtitle={isLV
              ? 'Reģistratorā atrodi "Redirect" / "URL Forwarding" / "Page Rules" un izveido pagaidu (302) pāradresāciju.'
              : 'In your registrar find "Redirect" / "URL Forwarding" / "Page Rules" and create a temporary (302) redirect.'} />

          <div className={`${t.soft} rounded-xl p-4 mb-4`}>
            <RedirectRecord
              from={state.domain}
              to={state.redirect_target}
              onCopy={copyToClipboard}
              isLV={isLV}
              t={t}
              dst={dst}
            />
            <RegistrarLinks isLV={isLV} t={t} />
            <button
              onClick={downloadPdf}
              className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-white border border-[#D97736] text-[#D97736] rounded-lg hover:bg-[#FFF8F0] transition-colors"
              data-testid="custom-domain-download-pdf"
            >
              <FileDown className="w-3.5 h-3.5" />
              {isLV ? 'Lejupielādēt PDF instrukcijas' : 'Download PDF instructions'}
            </button>
          </div>

          <StepHeader stepNumber={4} totalSteps={4} isLV={isLV} t={t}
            title={isLV ? 'Pārbaudi verifikāciju' : 'Verify'}
            subtitle={isLV
              ? 'Kad TXT ieraksts un pāradresācija ir pievienoti, nospied pogu zemāk. Mēs pārbaudīsim TXT ierakstu reālajā laikā.'
              : 'Once the TXT record and redirect are added, click below. We check your TXT record in real time.'} />

          <div className={`flex items-center gap-2 text-xs ${t.body} mb-3`}>
            <Clock className="w-3.5 h-3.5" />
            <span>
              {isLV
                ? 'DNS izmaiņas parasti aizņem 5-30 minūtes, dažreiz līdz 24 stundām.'
                : 'DNS changes usually take 5-30 minutes, sometimes up to 24 hours.'}
            </span>
          </div>

          <Button
            onClick={verify}
            disabled={verifying}
            className="w-full bg-[#D97736] hover:bg-[#C06528] text-white mb-3"
            data-testid="custom-domain-verify-btn"
          >
            {verifying ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {isLV ? 'Pārbauda DNS...' : 'Checking DNS...'}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <ExternalLink className="w-4 h-4" />
                {isLV ? 'Pārbaudīt verifikāciju' : 'Check verification'}
              </span>
            )}
          </Button>

          <Faq isLV={isLV} t={t} dst={dst} />
        </>
      )}

      {state.verified && (
        <div className={`mt-2 p-4 ${t.okPanel} rounded-xl text-sm`}>
          <div className="flex items-start gap-2">
            <Lightbulb className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <strong>{isLV ? 'Domēns ir aktīvs!' : 'Domain is live!'}</strong>{' '}
              {isLV
                ? `Pārliecinies, ka 302 pāradresācija reģistratorā ir aktīva. Katrs apmeklētājs, kas atvērs Tavu domēnu, tiks novirzīts uz Tavu ${dst}.`
                : `Make sure the 302 redirect is active at your registrar. Every visitor who opens your domain will be redirected to your ${dst}.`}
              <div className="mt-2 flex items-center gap-2 font-mono text-xs break-all opacity-90">
                <span>{state.domain}</span>
                <ArrowRight className="w-3.5 h-3.5 shrink-0" />
                <span>{state.redirect_target}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Theme tokens — light (shop profile) vs dark (business dashboard).
// ============================================================================
function themeTokens(dark) {
  return dark ? {
    card: 'bg-white/5 border border-white/10',
    title: 'text-white',
    body: 'text-white/55',
    iconWrap: 'bg-amber-500/15',
    soft: 'bg-white/[0.04] border border-white/10',
    softInner: 'bg-black/30 border border-white/10',
    input: 'bg-white/5 border-white/10 text-white placeholder:text-white/30',
    details: 'bg-white/[0.03] border border-white/10',
    summaryText: 'text-white',
    summaryHover: 'hover:bg-white/5',
    okWrap: 'bg-emerald-500/15',
    okColor: 'text-emerald-400',
    okBadge: 'bg-emerald-500/20 text-emerald-300',
    okText: 'text-emerald-300',
    okPanel: 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-200',
    warnWrap: 'bg-amber-500/15',
    warnColor: 'text-amber-400',
    warnBadge: 'bg-amber-500/20 text-amber-300',
    recLabel: 'text-white/40',
    recValue: 'text-white',
    recType: 'bg-[#D97736] text-white',
    copy: 'text-white/40 hover:text-white',
    copyHover: 'hover:bg-white/10',
    divider: 'border-white/10',
    pillBtn: 'bg-white/5 border border-white/10 text-white/80 hover:bg-white/10',
    stepCircle: 'bg-[#D97736] text-white',
  } : {
    card: 'bg-white border border-[#E5E4E0]',
    title: 'text-[#121212]',
    body: 'text-[#5C5C5C]',
    iconWrap: 'bg-[#FFF8F0]',
    soft: 'bg-[#FFF8F0] border border-[#FBD8B5]',
    softInner: 'bg-white border border-[#E5E4E0]',
    input: '',
    details: 'bg-[#FAFAFA] border border-[#E5E4E0]',
    summaryText: 'text-[#121212]',
    summaryHover: 'hover:bg-[#F0EFEC]',
    okWrap: 'bg-emerald-50',
    okColor: 'text-emerald-600',
    okBadge: 'bg-emerald-100 text-emerald-700',
    okText: 'text-emerald-700',
    okPanel: 'bg-emerald-50 border border-emerald-200 text-emerald-800',
    warnWrap: 'bg-amber-50',
    warnColor: 'text-amber-600',
    warnBadge: 'bg-amber-100 text-amber-700',
    recLabel: 'text-[#5C5C5C]',
    recValue: 'text-[#121212]',
    recType: 'bg-[#D97736] text-white',
    copy: 'text-[#5C5C5C] hover:text-[#121212]',
    copyHover: 'hover:bg-[#F0EFEC]',
    divider: 'border-[#FBD8B5]',
    pillBtn: 'bg-white border border-[#FBD8B5] text-[#121212] hover:bg-[#FFF8F0]',
    stepCircle: 'bg-[#D97736] text-white',
  };
}

// ============================================================================
// Sub-components
// ============================================================================

function HowItWorks({ isLV, t, dst }) {
  const steps = isLV ? [
    { title: 'Pievieno domēnu', desc: 'Ievadi savu domēnu (piem., mans-veikals.lv) un nospied Sākt.' },
    { title: 'Pievieno TXT ierakstu', desc: 'Mēs parādīsim TXT ierakstu, kas apstiprina, ka domēns pieder Tev.' },
    { title: 'Izveido 302 pāradresāciju', desc: `Reģistratorā novirzi savu domēnu uz Tavu ${dst} (302 pagaidu redirect).` },
    { title: 'Pārbaudi — un gatavs!', desc: 'Nospied "Pārbaudīt". Domēns sāk strādāt uzreiz — bez gaidīšanas.' },
  ] : [
    { title: 'Add your domain', desc: 'Enter your domain (e.g., my-shop.com) and click Start.' },
    { title: 'Add a TXT record', desc: 'We show a TXT record that confirms you own the domain.' },
    { title: 'Set up a 302 redirect', desc: `In your registrar, redirect your domain to your ${dst} URL (302 temporary redirect).` },
    { title: 'Verify — and done!', desc: 'Click "Verify". The domain works immediately — no waiting.' },
  ];

  return (
    <details className={`group mb-1 ${t.details} rounded-xl`} data-testid="how-it-works">
      <summary className={`cursor-pointer list-none px-4 py-3 flex items-center justify-between font-medium ${t.summaryText} ${t.summaryHover} rounded-xl`}>
        <span className="flex items-center gap-2">
          <HelpCircle className="w-4 h-4 text-[#D97736]" />
          {isLV ? 'Kā tas strādā? 4 vienkārši soļi' : 'How does it work? 4 simple steps'}
        </span>
        <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
      </summary>
      <ol className="px-4 pb-4 pt-1 space-y-3">
        {steps.map((s, i) => (
          <li key={i} className="flex gap-3 text-sm">
            <span className={`w-6 h-6 rounded-full ${t.stepCircle} text-xs font-bold flex items-center justify-center shrink-0`}>
              {i + 1}
            </span>
            <div>
              <div className={`font-semibold ${t.title}`}>{s.title}</div>
              <div className={t.body}>{s.desc}</div>
            </div>
          </li>
        ))}
      </ol>
    </details>
  );
}

function StepHeader({ stepNumber, totalSteps, title, subtitle, t }) {
  return (
    <div className="flex items-start gap-3 mb-3">
      <div className={`w-8 h-8 rounded-full ${t.stepCircle} text-sm font-bold flex items-center justify-center shrink-0`}>
        {stepNumber}
      </div>
      <div className="flex-1 pt-0.5">
        <div className={`font-semibold ${t.title} text-sm`}>{title}</div>
        <div className={`text-xs ${t.body} mt-0.5`}>{subtitle}</div>
      </div>
      <div className={`text-xs ${t.body} shrink-0`}>
        {stepNumber} / {totalSteps}
      </div>
    </div>
  );
}

function RegistrarLinks({ isLV, t }) {
  const registrars = [
    { name: 'Cloudflare', helpUrl: 'https://developers.cloudflare.com/rules/url-forwarding/single-redirects/create-dashboard/' },
    { name: 'NIC.LV', helpUrl: 'https://www.nic.lv/lv/kontakti' },
    { name: 'GoDaddy', helpUrl: 'https://www.godaddy.com/help/forward-my-godaddy-domain-12123' },
    { name: 'Namecheap', helpUrl: 'https://www.namecheap.com/support/knowledgebase/article.aspx/385/2237/how-to-make-a-url-redirect-for-a-domain/' },
  ];
  return (
    <div className={`mt-3 pt-3 border-t ${t.divider}`}>
      <div className={`text-xs font-semibold ${t.body} mb-2`}>
        {isLV ? 'Kā izveidot pāradresāciju (populāri reģistratori):' : 'How to set up a redirect (popular registrars):'}
      </div>
      <div className="flex flex-wrap gap-2">
        {registrars.map(r => (
          <a
            key={r.name}
            href={r.helpUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs ${t.pillBtn} rounded-full transition-colors`}
          >
            {r.name} <ExternalLink className="w-3 h-3" />
          </a>
        ))}
      </div>
    </div>
  );
}

function Faq({ isLV, t, dst }) {
  const items = isLV ? [
    {
      q: 'Cik ilgi aizņem DNS izplatīšanās?',
      a: 'TXT ieraksta verifikācija parasti notiek 5-30 minūtēs, dažreiz līdz 24 stundām. Atkarīgs no reģistratora un TTL vērtības.',
    },
    {
      q: 'Vai mans domēns vairs darbosies pēc pievienošanas?',
      a: `TXT ieraksts neko nemaina uz Tava domēna. 302 pāradresācija novirzīs apmeklētājus uz Tavu ${dst} — pievieno to tikai tad, kad esi gatavs.`,
    },
    {
      q: 'Kāpēc 302, nevis CNAME?',
      a: 'CNAME prasītu KoLV pusē konfigurēt domēnu mūsu serveros un SSL sertifikātu, ko mūsu platforma neatbalsta vairāku domēnu gadījumā. 302 pāradresācija strādā uzreiz un to pilnībā pārvalda Tavs reģistrators.',
    },
    {
      q: 'Vai SSL/HTTPS strādās?',
      a: 'Jā — pēc pāradresācijas apmeklētāji nonāk uz kolv.lv, kas jau ir aizsargāts ar HTTPS. Tavs domēns vienkārši novirza uz turieni.',
    },
    {
      q: 'Vai varu noņemt domēnu vēlāk?',
      a: 'Jā — jebkurā brīdī ar atkritnes pogu šajā lapā. Pēc tam atslēdz arī 302 pāradresāciju savā reģistratorā.',
    },
    {
      q: 'Kas notiek, ja kāds cits jau ir piereģistrējis manu domēnu?',
      a: 'Tu saņemsi kļūdas paziņojumu. Ja tas patiešām ir Tavs domēns, sazinies ar atbalsta dienestu kolatvietim@gmail.com ar īpašuma pierādījumu.',
    },
  ] : [
    {
      q: 'How long does DNS propagation take?',
      a: 'TXT verification usually completes in 5-30 minutes, sometimes up to 24 hours. Depends on your registrar and TTL.',
    },
    {
      q: 'Will my domain stop working when I add this?',
      a: `The TXT record changes nothing. The 302 redirect will forward visitors to your ${dst} — only add it when you are ready.`,
    },
    {
      q: 'Why 302 instead of CNAME?',
      a: 'A CNAME would require KoLV to configure your domain on our servers + an SSL cert, which our platform does not support for multiple custom domains. A 302 redirect works instantly and is fully managed by your registrar.',
    },
    {
      q: 'Will SSL/HTTPS work?',
      a: 'Yes — after the redirect, visitors land on kolv.lv which is already HTTPS-secured. Your domain simply forwards there.',
    },
    {
      q: 'Can I remove the domain later?',
      a: 'Yes — at any time via the trash button on this page. Then also disable the 302 redirect at your registrar.',
    },
    {
      q: 'What if someone else already registered my domain?',
      a: 'You will get an error. If the domain really is yours, contact support at kolatvietim@gmail.com with proof of ownership.',
    },
  ];

  return (
    <details className={`mt-2 ${t.details} rounded-xl`} data-testid="custom-domain-faq">
      <summary className={`cursor-pointer list-none px-4 py-3 flex items-center justify-between font-medium ${t.summaryText} ${t.summaryHover} rounded-xl`}>
        <span className="flex items-center gap-2">
          <HelpCircle className="w-4 h-4 text-[#D97736]" />
          {isLV ? 'Biežāk uzdotie jautājumi' : 'Frequently asked questions'}
        </span>
        <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" />
      </summary>
      <div className="px-4 pb-4 pt-1 space-y-3">
        {items.map((item, i) => (
          <div key={i} className="text-sm">
            <div className={`font-semibold ${t.title} mb-0.5`}>{item.q}</div>
            <div className={t.body}>{item.a}</div>
          </div>
        ))}
      </div>
    </details>
  );
}

function DnsRecord({ type, name, value, ttl, hint, onCopy, t }) {
  return (
    <div className={`${t.softInner} rounded-lg px-3 py-2.5`} data-testid="dns-record-txt">
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`px-2 py-0.5 text-xs font-bold ${t.recType} rounded`}>{type}</span>
        {hint && <span className={`text-xs ${t.body} italic`}>{hint}</span>}
        <button
          onClick={() => onCopy(`${type} ${name} ${value}`)}
          className={`ml-auto ${t.copy} p-1 rounded ${t.copyHover}`}
          title="Copy whole record"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="font-mono text-xs space-y-1">
        <div className="flex items-start gap-2 break-all">
          <span className={`${t.recLabel} shrink-0 w-12`}>Name:</span>
          <span className={`flex-1 ${t.recValue}`}>{name}</span>
          <button onClick={() => onCopy(name)} className={`${t.copy} shrink-0`} title="Copy name">
            <Copy className="w-3 h-3" />
          </button>
        </div>
        <div className="flex items-start gap-2 break-all">
          <span className={`${t.recLabel} shrink-0 w-12`}>Value:</span>
          <span className={`flex-1 ${t.recValue}`}>{value}</span>
          <button onClick={() => onCopy(value)} className={`${t.copy} shrink-0`} title="Copy value">
            <Copy className="w-3 h-3" />
          </button>
        </div>
        {ttl && (
          <div className="flex items-start gap-2">
            <span className={`${t.recLabel} shrink-0 w-12`}>TTL:</span>
            <span className={t.recValue}>{ttl}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function RedirectRecord({ from, to, onCopy, isLV, t, dst }) {
  return (
    <div className={`${t.softInner} rounded-lg px-3 py-2.5`} data-testid="redirect-record">
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`px-2 py-0.5 text-xs font-bold ${t.recType} rounded`}>302</span>
        <span className={`text-xs ${t.body} italic`}>
          {isLV ? `Novirza apmeklētājus uz Tavu ${dst}` : `Redirects visitors to your ${dst}`}
        </span>
        <button
          onClick={() => onCopy(`${from} -> ${to}`)}
          className={`ml-auto ${t.copy} p-1 rounded ${t.copyHover}`}
          title="Copy whole redirect"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="font-mono text-xs space-y-1">
        <div className="flex items-start gap-2 break-all">
          <span className={`${t.recLabel} shrink-0 w-16`}>{isLV ? 'No:' : 'From:'}</span>
          <span className={`flex-1 ${t.recValue}`}>{from}</span>
          <button onClick={() => onCopy(from)} className={`${t.copy} shrink-0`} title="Copy source">
            <Copy className="w-3 h-3" />
          </button>
        </div>
        <div className="flex items-start gap-2 break-all">
          <span className={`${t.recLabel} shrink-0 w-16`}>{isLV ? 'Uz:' : 'To:'}</span>
          <span className={`flex-1 ${t.recValue}`}>{to}</span>
          <button onClick={() => onCopy(to)} className={`${t.copy} shrink-0`} title="Copy destination">
            <Copy className="w-3 h-3" />
          </button>
        </div>
        <div className="flex items-start gap-2">
          <span className={`${t.recLabel} shrink-0 w-16`}>{isLV ? 'Tips:' : 'Type:'}</span>
          <span className={t.recValue}>302 ({isLV ? 'pagaidu' : 'temporary'})</span>
        </div>
      </div>
    </div>
  );
}
